export const API_URL = (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/$/, '')
  ?? 'https://api.spectraguardai.com:8443/api'

// During local browser validation, Vite proxies API traffic so production can
// keep its strict CORS allow-list. Explicit VITE_API_URL values still win.
const API_FETCH_URL = import.meta.env.DEV && !import.meta.env.VITE_API_URL
  ? '/backend-api/api'
  : API_URL

export class ApiError extends Error {
  constructor(public readonly code: string, public readonly status: number) {
    super(code)
  }
}

export async function apiBlob(path: string): Promise<Blob> {
  const headers = new Headers()
  if (accessToken) headers.set('Authorization', `Bearer ${accessToken}`)
  const response = await fetch(`${API_FETCH_URL}${path}`, { headers, credentials: 'include', cache: 'no-store' })
  if (!response.ok) throw new ApiError('download_error', response.status)
  return response.blob()
}

export async function apiBlobWithProgress(path: string, onProgress: (received: number, total: number | null) => void, signal?: AbortSignal): Promise<Blob> {
  const headers = new Headers()
  if (accessToken) headers.set('Authorization', `Bearer ${accessToken}`)
  const response = await fetch(`${API_FETCH_URL}${path}`, { headers, credentials: 'include', cache: 'no-store', signal })
  if (!response.ok) throw new ApiError('download_error', response.status)
  const totalHeader = response.headers.get('content-length')
  const total = totalHeader ? Number(totalHeader) : null
  if (!response.body) return response.blob()
  const reader = response.body.getReader()
  const chunks: BlobPart[] = []
  let received = 0
  try {
    while (true) {
      const { done, value } = await reader.read()
      if (done) break
      const copy = new Uint8Array(value.byteLength)
      copy.set(value)
      chunks.push(copy.buffer)
      received += value.byteLength
      onProgress(received, total)
    }
  } finally { reader.releaseLock() }
  return new Blob(chunks, { type: response.headers.get('content-type') ?? 'application/octet-stream' })
}

let accessToken: string | null = null

export function adoptAccessToken(token: string | null) {
  accessToken = token
}

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers)
  if (init.body && !(init.body instanceof FormData) && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json')
  if (accessToken) headers.set('Authorization', `Bearer ${accessToken}`)

  const response = await fetch(`${API_FETCH_URL}${path}`, {
    ...init,
    headers,
    credentials: 'include',
  })
  const payload = response.status === 204 || !response.headers.get('content-type')?.includes('json')
    ? null
    : await response.json()
  if (!response.ok) {
    const code = payload && typeof payload === 'object' && 'code' in payload
      ? String(payload.code)
      : 'generic_error'
    throw new ApiError(code, response.status)
  }
  return payload as T
}
